"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MessageCircle,
  MessagesSquare,
  Mic,
  ClipboardList,
  Phone,
  Sparkles,
  Video,
  X,
  AtSign,
  type LucideIcon,
} from "lucide-react";
import {
  isEmailInboxProvisioned,
} from "~/lib/marketplace/agent-email-provisioned";
import { api } from "~/trpc/react";
import { AiAgentEmailSignature } from "~/components/ai-agent/ai-agent-email-signature";
import { Button } from "~/components/ui/button";
import { normalizeAgentAvatarSrc } from "~/lib/agent-avatar-url";
import {
  isCallMePhoneProvisioned,
  isSmsProvisioned,
} from "~/lib/marketplace/agent-phone-provisioned";
import {
  SITEGLUE_LANDING_SITE_AGENT_PROJECT_ID,
  SITEGLUE_VICTORIA_TEXT_SMS_E164,
} from "~/lib/marketplace/constants";
import {
  AGENT_PROFILE_SIDEBAR_DEFAULT_WIDTH_PX,
  AGENT_PROFILE_SIDEBAR_MAX_WIDTH_PX,
  AGENT_PROFILE_SIDEBAR_MIN_WIDTH_PX,
  AGENT_SIDEBAR_OPEN_EVENT,
  getAgentProfileProjectId,
  storeSidebarWidthPx,
  type AgentSidebarOpenDetail,
} from "~/lib/marketplace/site-agent-sidebar";
import { cn } from "~/lib/utils";
import {
  buildChatOfficeStatusFromProject,
  buildLauncherBubbleWelcomeMessageFromProject,
} from "~/lib/chat/time-of-day-chat-welcome-message";
import { buildDefaultWidgetLead } from "~/lib/widget-location-lead";
import {
  buildAgentResponsibilities,
  getProjectCtaQuestions,
} from "~/lib/agent/agent-responsibilities";
import { isWhatsAppChannelLive } from "~/lib/channels/whatsapp-availability";
import {
  PROJECT_CTA_UPDATED_EVENT,
  WIDGET_CTA_REFRESH_MESSAGE_TYPE,
  type ProjectCtaUpdatedDetail,
} from "~/lib/easygpt-widget-reload";

export const LANDING_SIDEBAR_EXPANDED_WIDTH = "min(470px,36vw)";
export const LANDING_SIDEBAR_COLLAPSED_WIDTH = "min(410px,32vw)";

function getOrCreateEmbeddedWidgetSessionId(projectId: string): string | null {
  if (typeof window === "undefined") return null;
  const key = `ez-gpt-session-${projectId}`;
  const stored = sessionStorage.getItem(key)?.trim();
  if (stored) return stored;
  const id = crypto.randomUUID();
  sessionStorage.setItem(key, id);
  return id;
}

export type SidebarMode = "expanded" | "collapsed" | "closed";
export type SidebarWidgetTab =
  | "aiChat"
  | "talk"
  | "videoCall"
  | "welcomeVideo"
  | "contact"
  | "call"
  | "sms"
  | "email"
  | "whatsapp"
  | "messenger";

type LandingAgentSidebarProps = {
  projectId: string;
  layout?: "site" | "agent-profile";
  mode: SidebarMode;
  onModeChange: (mode: SidebarMode) => void;
  sidebarWidthPx?: number;
  onSidebarWidthChange?: (widthPx: number) => void;
  /** When set, embedded widget iframe opens on this tab (e.g. aiChat on home). */
  defaultWidgetTab?: SidebarWidgetTab;
  /** Fixed viewport sidebar (default) or contained within a relative parent. */
  position?: "fixed" | "contained";
};

function useMobileSidebar(breakpoint = 1023) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const update = () => setIsMobile(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [breakpoint]);

  return isMobile;
}

/** Build an `sms:` deep link for the device Messages app. */
function buildSmsAppHref(phone: string | null | undefined): string | null {
  const digits = (phone ?? "").replace(/\D/g, "");
  if (digits.length < 10) return null;
  const e164 =
    digits.length === 10
      ? `+1${digits}`
      : digits.length === 11 && digits.startsWith("1")
        ? `+${digits}`
        : `+${digits}`;
  return `sms:${e164}`;
}

export function LandingAgentSidebar({
  projectId,
  layout = "site",
  mode,
  onModeChange,
  sidebarWidthPx = AGENT_PROFILE_SIDEBAR_DEFAULT_WIDTH_PX,
  onSidebarWidthChange,
  defaultWidgetTab = "aiChat",
  position = "fixed",
}: LandingAgentSidebarProps) {
  const isContained = position === "contained";
  const isMobile = useMobileSidebar();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const pendingMessageRef = useRef<string | null>(null);
  const pendingTabRef = useRef<SidebarWidgetTab | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [sidebarInitialTab, setSidebarInitialTab] =
    useState<SidebarWidgetTab>(defaultWidgetTab);
  const [isLauncherHovered, setIsLauncherHovered] = useState(false);
  const [launcherPopupDismissed, setLauncherPopupDismissed] = useState(false);
  const isResizingRef = useRef(false);
  const [embeddedSessionId] = useState(() => getOrCreateEmbeddedWidgetSessionId(projectId));

  const projectQuery = api.project.publicGetProjectById.useQuery({
    id: projectId,
  });

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ProjectCtaUpdatedDetail>).detail;
      if (detail?.projectId !== projectId) return;
      void projectQuery.refetch();
      iframeRef.current?.contentWindow?.postMessage(
        { type: WIDGET_CTA_REFRESH_MESSAGE_TYPE, projectId },
        "*",
      );
    };
    window.addEventListener(PROJECT_CTA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(PROJECT_CTA_UPDATED_EVENT, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch is stable; avoid resubscribing every render
  }, [projectId]);

  const { data: phoneAgents } = api.vapiAssistant.getAgentsForUser.useQuery({
    projectId,
  });

  const agent = projectQuery.data?.aiAgent;
  const visualSettings = projectQuery.data?.visualSettings;
  const channels = visualSettings?.channels as Record<string, boolean | undefined> | undefined;
  const talkAnamVideoEnabled = visualSettings?.talkAnamVideoEnabled === true;
  const agentName = agent?.name ?? "Easy GPT AI Agent";
  const agentFirstName = agentName.split(" ")[0] ?? agentName;
  const agentTitle = agent?.jobTitle ?? "AI concierge";
  const agentAvatarSrc = normalizeAgentAvatarSrc(agent?.base64Avatar);
  const accentColor = visualSettings?.widgetButtonColor ?? "#2563eb";
  const headerColor = visualSettings?.headerColor ?? "#D9AC48";
  const companyName = projectQuery.data?.title ?? "";
  const officeStatus = useMemo(() => {
    if (!projectQuery.data) return null;
    const lead = buildDefaultWidgetLead(
      null,
      projectQuery.data.projectLocation as unknown[],
    );
    return buildChatOfficeStatusFromProject({
      visualSettings,
      projectLocations: projectQuery.data.projectLocation as unknown[],
      leadIsAvailable: lead?.isAvailable,
    });
  }, [projectQuery.data, visualSettings]);
  const projectCtaQuestions = useMemo(
    () => getProjectCtaQuestions(projectQuery.data?.projectSettings),
    [projectQuery.data?.projectSettings],
  );
  const howCanIHelpButtons = useMemo(
    () => ["Ask a question", ...projectCtaQuestions],
    [projectCtaQuestions],
  );
  const launcherWelcomeMessage = useMemo(() => {
    const resolvedFromServer = (
      projectQuery.data as { resolvedLauncherWelcomeMessage?: string | null } | undefined
    )?.resolvedLauncherWelcomeMessage?.trim();
    if (resolvedFromServer) {
      return resolvedFromServer;
    }

    if (!projectQuery.data) {
      return visualSettings?.startingMessage?.trim() || "Hi! How can I help you today?";
    }
    const lead = buildDefaultWidgetLead(
      null,
      projectQuery.data.projectLocation as unknown[],
    );
    return buildLauncherBubbleWelcomeMessageFromProject({
      visualSettings,
      aiAgent: agent,
      projectTitle: projectQuery.data.title,
      ctaClusters: projectQuery.data.projectSettings?.[0]?.ctaClusters ?? [],
      existingLocations: null,
      projectLocations: projectQuery.data.projectLocation as unknown[],
      leadIsAvailable: lead?.isAvailable,
    });
  }, [agent, projectQuery.data, visualSettings]);
  const showLauncherPopup =
    visualSettings?.widgetPopupEnabled !== false &&
    !launcherPopupDismissed &&
    (!isMobile || layout === "agent-profile");
  const enabledResponsibilities = useMemo(
    () =>
      buildAgentResponsibilities(
        projectQuery.data?.visualSettings?.channels as
          | Record<string, boolean>
          | undefined,
      ).filter((role) => role.enabled),
    [projectQuery.data?.visualSettings?.channels],
  );
  const talkEnabled = channels?.talk === true;
  const callMePhoneProvisioned = useMemo(() => {
    const phoneAgent = phoneAgents?.[0];
    if (!phoneAgent) return false;
    return isCallMePhoneProvisioned(phoneAgent);
  }, [phoneAgents]);
  const smsPhoneProvisioned = useMemo(() => {
    const phoneAgent = phoneAgents?.[0];
    if (!phoneAgent) return false;
    return isSmsProvisioned(phoneAgent);
  }, [phoneAgents]);
  const textAgentSmsHref = useMemo(() => {
    // SiteGlue marketing FAB: always text Victoria's public SMS inbox (not a stale voice line).
    if (projectId === SITEGLUE_LANDING_SITE_AGENT_PROJECT_ID) {
      return buildSmsAppHref(SITEGLUE_VICTORIA_TEXT_SMS_E164);
    }
    const phoneAgent = phoneAgents?.[0];
    if (!phoneAgent || !isSmsProvisioned(phoneAgent)) return null;
    // Prefer the dedicated SMS number — do not fall back to the voice line.
    return buildSmsAppHref(phoneAgent.smsPhoneNumber?.trim() || null);
  }, [phoneAgents, projectId]);
  const emailInboxProvisioned = useMemo(
    () => isEmailInboxProvisioned(projectQuery.data?.notificationSettings),
    [projectQuery.data?.notificationSettings],
  );

  const widgetTabBubbles = useMemo(() => {
    const bubbles: { tab: SidebarWidgetTab; label: string; icon: LucideIcon }[] = [];

    if (channels?.aiChat !== false) {
      bubbles.push({ tab: "aiChat", label: "Chat", icon: MessageCircle });
    }
    if (talkEnabled) {
      bubbles.push({ tab: "talk", label: "Talk", icon: Mic });
    }
    if (talkEnabled && talkAnamVideoEnabled) {
      bubbles.push({ tab: "videoCall", label: "Video Call", icon: Video });
    }
    if (channels?.contact === true) {
      bubbles.push({ tab: "contact", label: "Contact Us", icon: ClipboardList });
    }
    if (channels?.call === true && callMePhoneProvisioned) {
      bubbles.push({ tab: "call", label: "Call Me", icon: Phone });
    }
    if (channels?.sms === true && smsPhoneProvisioned) {
      bubbles.push({ tab: "sms", label: "Text Me", icon: MessagesSquare });
    }
    if (channels?.email === true && emailInboxProvisioned) {
      bubbles.push({ tab: "email", label: "Email Me", icon: AtSign });
    }
    if (isWhatsAppChannelLive(channels)) {
      bubbles.push({ tab: "whatsapp", label: "WhatsApp", icon: MessageCircle });
    }

    return bubbles;
  }, [
    channels?.aiChat,
    channels?.call,
    channels?.contact,
    channels?.sms,
    channels?.email,
    channels?.whatsapp,
    talkEnabled,
    talkAnamVideoEnabled,
    callMePhoneProvisioned,
    smsPhoneProvisioned,
    emailInboxProvisioned,
  ]);

  const widgetSrc = useMemo(() => {
    const origin =
      typeof window !== "undefined"
        ? window.location.origin
        : "https://app.easygpt.builders";
    const params = new URLSearchParams({
      full: "true",
      scroll: "top",
      embeddedSidebar: "1",
      initialTab: sidebarInitialTab,
    });
    if (embeddedSessionId) {
      params.set("sessionId", embeddedSessionId);
    }
    return `${origin}/w/${projectId}?${params.toString()}`;
  }, [projectId, sidebarInitialTab, embeddedSessionId]);

  const closeSidebar = useCallback(() => {
    onModeChange("closed");
  }, [onModeChange]);

  const postToWidget = useCallback((payload: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage(payload, "*");
  }, []);

  const expandSidebar = useCallback(
    (options?: { message?: string; tab?: SidebarWidgetTab }) => {
      const tab: SidebarWidgetTab = options?.message
        ? "aiChat"
        : options?.tab ?? "aiChat";

      if (options?.message) {
        pendingMessageRef.current = options.message;
      }
      pendingTabRef.current = tab;
      setSidebarInitialTab(tab);

      const iframeReady = Boolean(iframeRef.current?.contentWindow);
      if (mode !== "closed" && iframeLoaded && iframeReady) {
        postToWidget({ type: "switchTab", tab });
        pendingTabRef.current = null;

        if (options?.message) {
          const message = options.message;
          pendingMessageRef.current = null;
          window.setTimeout(() => {
            postToWidget({ type: "SEND_MESSAGE_TO_CHAT", message });
          }, 700);
        }

        if (mode !== "expanded") {
          onModeChange("expanded");
        }
        return;
      }

      onModeChange("expanded");
    },
    [iframeLoaded, mode, onModeChange, postToWidget],
  );

  const isAgentProfileLayout = layout === "agent-profile";

  const collapseSidebar = useCallback(() => {
    if (isAgentProfileLayout) {
      closeSidebar();
      return;
    }
    onModeChange("collapsed");
  }, [closeSidebar, isAgentProfileLayout, onModeChange]);

  const openSidebar = useCallback(() => {
    expandSidebar({ tab: defaultWidgetTab });
  }, [defaultWidgetTab, expandSidebar]);

  const dismissLauncherPopup = useCallback(() => {
    setLauncherPopupDismissed(true);
    if (typeof window !== "undefined") {
      localStorage.setItem(`easygpt-bubble-closed-${projectId}`, "true");
    }
  }, [projectId]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "openMailto" && typeof event.data.email === "string") {
        const mailtoUrl = `mailto:${event.data.email}`;
        const anchor = document.createElement("a");
        anchor.href = mailtoUrl;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        return;
      }
      if (event.data?.type === "landingSidebarCollapse" || event.data?.type === "widgetClosed") {
        if (isMobile) {
          closeSidebar();
        } else {
          collapseSidebar();
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [collapseSidebar, closeSidebar, isMobile]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setLauncherPopupDismissed(
      localStorage.getItem(`easygpt-bubble-closed-${projectId}`) === "true",
    );
  }, [projectId]);

  useEffect(() => {
    if (!isMobile || mode === "closed") return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobile, mode]);

  useEffect(() => {
    if (mode === "closed") {
      setIframeLoaded(false);
    }
  }, [mode]);

  useEffect(() => {
    setIframeLoaded(false);
  }, [projectId]);

  useEffect(() => {
    const handleOpen = (event: Event) => {
      const detail = (event as CustomEvent<AgentSidebarOpenDetail>).detail;
      const profileProjectId =
        typeof window !== "undefined"
          ? getAgentProfileProjectId(window.location.pathname)
          : null;

      if (
        detail?.projectId &&
        detail.projectId !== projectId &&
        detail.projectId !== profileProjectId
      ) {
        return;
      }

      if (detail?.message || detail?.tab) {
        expandSidebar({ message: detail.message, tab: detail.tab });
        return;
      }

      expandSidebar({ tab: "aiChat" });
    };

    window.addEventListener(AGENT_SIDEBAR_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(AGENT_SIDEBAR_OPEN_EVENT, handleOpen);
  }, [expandSidebar, projectId]);

  useEffect(() => {
    if (mode !== "expanded" || !iframeLoaded) return;

    const pendingTab = pendingTabRef.current;
    const pendingMessage = pendingMessageRef.current;

    if (pendingTab) {
      postToWidget({ type: "switchTab", tab: pendingTab });
      pendingTabRef.current = null;
    } else if (sidebarInitialTab !== defaultWidgetTab) {
      postToWidget({ type: "switchTab", tab: sidebarInitialTab });
    } else {
      postToWidget({ type: "switchTab", tab: defaultWidgetTab });
    }

    if (pendingMessage) {
      pendingMessageRef.current = null;
      const messageTimer = window.setTimeout(() => {
        postToWidget({ type: "SEND_MESSAGE_TO_CHAT", message: pendingMessage });
      }, pendingTab ? 700 : 600);

      return () => window.clearTimeout(messageTimer);
    }
  }, [mode, iframeLoaded, postToWidget, defaultWidgetTab, sidebarInitialTab]);

  /** Agent profile: FAB + CTAs on load; full widget iframe only after user opens chat. */
  const showAsidePanel =
    mode !== "closed" &&
    (!isAgentProfileLayout || mode === "expanded" || (isContained && mode === "collapsed"));
  const showCollapsedCtaPanel =
    mode === "collapsed" && (!isAgentProfileLayout || isContained);

  const sidebarWidth = isContained
    ? "100%"
    : !isMobile
      ? `${sidebarWidthPx}px`
      : mode === "expanded"
        ? LANDING_SIDEBAR_EXPANDED_WIDTH
        : LANDING_SIDEBAR_COLLAPSED_WIDTH;

  const handleResizeStart = useCallback(
    (event: React.MouseEvent) => {
      if (isMobile || !onSidebarWidthChange) return;
      event.preventDefault();
      isResizingRef.current = true;
      const startX = event.clientX;
      const startWidth = sidebarWidthPx;

      let latestWidth = startWidth;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isResizingRef.current) return;
        const delta = startX - moveEvent.clientX;
        latestWidth = Math.min(
          AGENT_PROFILE_SIDEBAR_MAX_WIDTH_PX,
          Math.max(AGENT_PROFILE_SIDEBAR_MIN_WIDTH_PX, startWidth + delta),
        );
        onSidebarWidthChange(latestWidth);
      };

      const handleMouseUp = () => {
        isResizingRef.current = false;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        storeSidebarWidthPx(projectId, latestWidth);
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [isMobile, onSidebarWidthChange, projectId, sidebarWidthPx],
  );

  const isOverlayOpen = mode !== "closed";
  /** Agent profile: no FAB/popup until Live Demo (or ?try=1) opens the sidebar. */
  const showClosedLauncher = mode === "closed" && !isAgentProfileLayout;
  const asidePositionClass =
    isContained
      ? "absolute inset-0 z-10 flex h-full w-full flex-col border-l border-border/80 bg-background shadow-none"
      : isMobile
      ? cn(
          "fixed inset-0 z-50 h-dvh w-full max-w-full border-0 shadow-none",
          !isAgentProfileLayout && "pt-16",
        )
      : cn(
          "fixed right-0 z-40 border-l border-border/80 shadow-2xl",
          isAgentProfileLayout
            ? "top-0 h-dvh"
            : mode === "collapsed"
              ? // Shrink-wrap collapsed CTA panel so empty aside height does not block page clicks.
                "top-16 h-auto max-h-[calc(100dvh-4rem)]"
              : "top-16 h-[calc(100dvh-4rem)]",
          "w-full max-w-full sm:w-[min(470px,100vw)] lg:max-w-none",
          mode === "collapsed" && !isAgentProfileLayout && "pointer-events-none",
        );

  const closeButton = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-8 gap-1 px-2 text-muted-foreground hover:text-foreground"
      onClick={closeSidebar}
      aria-label="Close agent sidebar"
    >
      <span className="text-xs font-medium">Close</span>
      <X className="h-4 w-4" />
    </Button>
  );

  return (
    <>
      {showClosedLauncher ? (
        <div
          className={cn(
            // Pass clicks through empty space in the launcher stack so page CTAs stay usable.
            "pointer-events-none z-50 flex max-w-[min(375px,calc(100%-2rem))] flex-col items-end gap-2",
            isContained
              ? "absolute bottom-4 right-4"
              : "fixed bottom-6 right-6 max-lg:bottom-5 max-lg:right-4",
          )}
        >
          {showLauncherPopup && !isLauncherHovered ? (
            <>
              <button
                type="button"
                onClick={dismissLauncherPopup}
                className="pointer-events-auto flex h-8 w-8 items-center justify-center rounded-full bg-white text-muted-foreground shadow-md transition hover:bg-muted hover:text-foreground"
                aria-label="Dismiss welcome message"
              >
                <X className="h-4 w-4" />
              </button>
              {projectCtaQuestions.length > 0 ? (
                <div className="pointer-events-auto flex w-full flex-col items-end gap-1.5">
                  {projectCtaQuestions.map((cta) => (
                    <button
                      key={cta}
                      type="button"
                      onClick={() => expandSidebar({ message: cta, tab: "aiChat" })}
                      className="rounded-md px-4 py-2 text-left text-sm font-medium text-white shadow-md transition hover:opacity-90 hover:shadow-lg"
                      style={{ backgroundColor: accentColor }}
                    >
                      {cta}
                    </button>
                  ))}
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => expandSidebar({ tab: "aiChat" })}
                className="pointer-events-auto relative w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left text-sm leading-relaxed text-slate-800 shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
              >
                {launcherWelcomeMessage}
                <span
                  aria-hidden
                  className="absolute -bottom-2 right-6 h-0 w-0 border-x-8 border-t-8 border-x-transparent border-t-white"
                />
                <span
                  aria-hidden
                  className="absolute -bottom-[9px] right-[23px] h-0 w-0 border-x-[9px] border-t-[9px] border-x-transparent border-t-slate-200"
                />
              </button>
            </>
          ) : null}
          <div
            className="pointer-events-auto flex flex-col items-end gap-2"
            onMouseEnter={() => setIsLauncherHovered(true)}
            onMouseLeave={() => setIsLauncherHovered(false)}
          >
            {widgetTabBubbles.length > 0 && isLauncherHovered && !isMobile ? (
              <div className="flex flex-col items-end gap-2">
                {widgetTabBubbles.map((bubble) => {
                  const Icon = bubble.icon;
                  return (
                    <button
                      key={bubble.tab}
                      type="button"
                      onClick={() => expandSidebar({ tab: bubble.tab })}
                      className="flex items-center gap-2 rounded-full border border-slate-200/80 bg-white px-3.5 py-2 text-xs font-semibold text-slate-800 shadow-md transition hover:scale-[1.02] hover:shadow-lg"
                      aria-label={`Open ${bubble.label}`}
                    >
                      <Icon className="h-4 w-4 shrink-0" style={{ color: accentColor }} />
                      {bubble.label}
                    </button>
                  );
                })}
              </div>
            ) : null}
            {isMobile && textAgentSmsHref ? (
              <a
                href={textAgentSmsHref}
                className="flex items-center gap-2 rounded-full border border-white/80 bg-white px-4 py-2.5 text-sm font-semibold shadow-lg transition-transform hover:scale-[1.02] hover:shadow-xl"
                style={{ color: accentColor }}
                aria-label={`Text ${agentFirstName}`}
              >
                <MessagesSquare className="h-4 w-4 shrink-0" aria-hidden />
                Text {agentFirstName}
              </a>
            ) : null}
            <button
              type="button"
              onClick={openSidebar}
              className="flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-[1.02] hover:shadow-xl max-lg:px-4 max-lg:py-2.5 max-lg:text-sm"
              style={{ backgroundColor: accentColor }}
              aria-label={`Ask ${agentFirstName}`}
            >
              <Sparkles className="h-4 w-4 shrink-0" />
              Ask {agentFirstName}
            </button>
          </div>
        </div>
      ) : null}

      {isMobile && showAsidePanel && !isContained ? (
        <button
          type="button"
          aria-label="Close agent overlay backdrop"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={closeSidebar}
        />
      ) : null}

      {showAsidePanel ? (
      <aside
        className={cn(
          "relative flex flex-col bg-background transition-[width] duration-300",
          asidePositionClass,
        )}
        style={isMobile ? undefined : { width: sidebarWidth }}
        aria-label={`${agentName} chat`}
      >
          {!isMobile && isOverlayOpen && onSidebarWidthChange ? (
            <div
              role="separator"
              aria-label="Resize chat sidebar"
              aria-orientation="vertical"
              className="pointer-events-auto absolute left-0 top-0 z-10 h-full w-2 -translate-x-1/2 cursor-col-resize touch-none hover:bg-primary/15 active:bg-primary/25"
              onMouseDown={handleResizeStart}
            />
          ) : null}
          {showCollapsedCtaPanel ? (
            <div className="pointer-events-auto flex h-11 shrink-0 items-center justify-between border-b bg-muted/40 px-4">
              <p className="truncate text-sm font-semibold text-foreground">{agentName}</p>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                  Live
                </span>
                {closeButton}
              </div>
            </div>
          ) : null}

          {showCollapsedCtaPanel ? (
            <div className="pointer-events-auto flex min-h-0 flex-1 flex-col overflow-y-auto">
              <div className="space-y-4 px-4 py-5">
                {isContained && widgetTabBubbles.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {widgetTabBubbles.map((bubble) => {
                      const Icon = bubble.icon;
                      return (
                        <Button
                          key={bubble.tab}
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          onClick={() => expandSidebar({ tab: bubble.tab })}
                        >
                          <Icon className="h-4 w-4 shrink-0" style={{ color: accentColor }} />
                          {bubble.label}
                        </Button>
                      );
                    })}
                  </div>
                ) : null}

                <AiAgentEmailSignature
                  agentName={agentName}
                  agentTitle={agentTitle}
                  companyName={companyName}
                  avatarSrc={agentAvatarSrc}
                  headerColor={headerColor}
                  phoneNumber={agent?.phoneNumber}
                  emailAddress={agent?.emailAddress}
                  websiteUrl={agent?.websiteUrl}
                />

                {officeStatus ? (
                  <p className="text-xs leading-relaxed text-muted-foreground">{officeStatus}</p>
                ) : null}

                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    How can I help
                  </p>
                  <div className="space-y-2">
                    {howCanIHelpButtons.map((label) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() =>
                          expandSidebar(
                            label === "Ask a question"
                              ? { tab: "aiChat" }
                              : { message: label, tab: "aiChat" },
                          )
                        }
                        className="w-full rounded-lg border bg-background px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:border-primary/40 hover:bg-primary/5"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  {talkEnabled ? (
                    <Button
                      type="button"
                      className="w-full justify-center gap-2 shadow-sm"
                      style={{ backgroundColor: accentColor }}
                      onClick={() => expandSidebar({ tab: "talk" })}
                    >
                      <Mic className="h-4 w-4" />
                      Speak with {agentFirstName}
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    className="w-full justify-center gap-2"
                    style={{ backgroundColor: accentColor }}
                    onClick={() => expandSidebar({ tab: "aiChat" })}
                  >
                    <MessageCircle className="h-4 w-4" />
                    Chat with {agentFirstName}
                  </Button>
                </div>

                {enabledResponsibilities.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      My Responsibilities
                    </p>
                    <div className="space-y-1.5">
                      {enabledResponsibilities.map((role) => (
                        <div
                          key={role.key}
                          className="rounded-lg border bg-background px-3 py-2"
                        >
                          <p className="text-xs font-medium text-foreground">{role.title}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {mode === "expanded" ? (
            <iframe
              key={`${projectId}-${sidebarInitialTab}`}
              ref={iframeRef}
              src={widgetSrc}
              title={`${agentName} live chat`}
              className="pointer-events-auto min-h-0 w-full flex-1 border-0"
              allow="microphone; camera; autoplay; clipboard-write"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
              onLoad={() => setIframeLoaded(true)}
            />
          ) : null}
        </aside>
      ) : null}
    </>
  );
}
